import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { ContractHistory } from "@frontier/protocol/contract-history";
import type { Profile } from "@frontier/protocol/contracts";
import type { ProfileSync } from "./profile-sync";
import { api } from "./session-api";

export interface ContractHistoryControls {
  history: ContractHistory | null;
  busy: boolean;
  error: string;
  resetRequired: boolean;
  reload: () => Promise<void>;
  refresh: () => Promise<void>;
  cancel: () => void;
  more: () => Promise<void>;
}

export function useContractHistory(
  profile: Profile | null,
  token: RefObject<string>,
  sync: ProfileSync,
): ContractHistoryControls {
  const owner = useRef(profile?.playerId);
  owner.current = profile?.playerId;
  const [state, setState] = useState<{
    playerId: string;
    token: string;
    ticket: number;
    history: ContractHistory | null;
    busy: boolean;
    error: string;
    resetRequired: boolean;
  } | null>(null);
  const current = useRef(state);
  current.current = state;
  const request = useRef<{ controller: AbortController; playerId: string; token: string; ticket: number } | null>(null);
  const sequence = useRef(0);
  const ticket = sync.ticket();
  const sessionToken = token.current;
  useEffect(() => () => { request.current?.controller.abort(); sequence.current++; }, []);
  const cancel = useCallback(() => {
    if (!request.current) return;
    request.current.controller.abort();
    request.current = null;
    sequence.current++;
    setState(previous => previous ? { ...previous, busy: false } : previous);
  }, []);

  const read = useCallback(async (mode: "replace" | "append" | "refresh") => {
    const playerId = owner.current;
    const session = token.current;
    const epoch = sync.ticket();
    if (!playerId || !session) return;
    const previous = current.current;
    const matching = previous?.playerId === playerId &&
      previous.token === session && previous.ticket === epoch;
    const history = matching ? previous.history : null;
    if (mode === "refresh" && matching && previous.resetRequired) return;
    const active = request.current;
    const sameOwner = active?.playerId === playerId && active.token === session && active.ticket === epoch;
    if (mode === "append" && (!history?.nextCursor || sameOwner)) return;
    if (mode === "refresh" && sameOwner) return;
    active?.controller.abort();
    const controller = new AbortController();
    request.current = { controller, playerId, token: session, ticket: epoch };
    const id = ++sequence.current;
    const owns = () => !controller.signal.aborted && sequence.current === id &&
      owner.current === playerId && token.current === session && sync.ticket() === epoch;
    const base = { playerId, token: session, ticket: epoch };
    setState({ ...base, history, busy: true, error: "", resetRequired: false });
    try {
      const page = await api.contractHistory(session, mode === "append" ? history?.nextCursor ?? undefined : undefined, controller.signal);
      if (!owns()) return;
      let entries = mode === "append" && history
        ? [...history.entries, ...page.entries.filter(entry => !history.entries.some(saved => saved.result.runId === entry.result.runId))]
        : page.entries;
      let nextCursor = page.nextCursor;
      if (mode === "refresh" && history?.entries.length) {
        const savedIds = new Set(history.entries.map(entry => entry.result.runId));
        let cursor = page.nextCursor;
        let overlap = page.entries.some(entry => savedIds.has(entry.result.runId));
        const visited = new Set<string>();
        while (!overlap && cursor) {
          if (visited.size >= 25) throw new HistoryRefreshLimitError();
          if (visited.has(cursor)) throw new Error("Не удалось обновить историю: повторный курсор страницы.");
          visited.add(cursor);
          const next = await api.contractHistory(session, cursor, controller.signal);
          if (!owns()) return;
          entries = [...entries, ...next.entries];
          overlap = next.entries.some(entry => savedIds.has(entry.result.runId));
          cursor = next.nextCursor;
        }
        if (overlap) {
          const lastMatch = entries.findLastIndex(entry => savedIds.has(entry.result.runId));
          const savedMatch = history.entries.findIndex(entry => entry.result.runId === entries[lastMatch]?.result.runId);
          entries = [...entries.slice(0, lastMatch + 1), ...history.entries.slice(savedMatch + 1)];
        }
        const seen = new Set<string>();
        entries = entries.filter(entry => {
          if (seen.has(entry.result.runId)) return false;
          seen.add(entry.result.runId);
          return true;
        });
        nextCursor = overlap ? history.nextCursor : null;
      }
      setState({ ...base, history: { entries, nextCursor }, busy: false, error: "", resetRequired: false });
    } catch (error) {
      if (!owns()) return;
      const message = error instanceof Error ? error.message : "Не удалось загрузить историю.";
      console.warn("contract-history-read-failed", { playerId, message });
      setState({ ...base, history, busy: false, error: message, resetRequired: error instanceof HistoryRefreshLimitError });
    } finally {
      if (request.current?.controller === controller) request.current = null;
    }
  }, [token, sync, profile?.playerId, sessionToken, ticket]);
  const reload = useCallback(() => read("replace"), [read]);
  const refresh = useCallback(() => read("refresh"), [read]);
  const more = useCallback(() => read("append"), [read]);
  const visible = state?.playerId === profile?.playerId && state?.token === sessionToken && state?.ticket === ticket;
  return { history: visible ? state.history : null, busy: visible && state.busy,
    error: visible ? state.error : "", resetRequired: visible && state.resetRequired, reload, refresh, cancel, more };
}

class HistoryRefreshLimitError extends Error {
  constructor() { super("Слишком много новых вылетов. Загрузи историю заново с первой страницы."); }
}
